import { useState } from "react";
import { Music2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Section, Param } from "@/components/editor/Section";
import { AssetList } from "@/components/editor/AssetList";
import { api, formatApiError } from "@/lib/api";

const REFS = ["Interstellar", "Inception", "The Dark Knight", "Oppenheimer", "Dune", "The Social Network", "Stranger Things", "Succession",
  "Game of Thrones", "The Last of Us", "Jaws", "Star Wars", "Harry Potter", "Breaking Bad", "Gladiator", "Joker"];
const MOODS = ["Epic", "Tense", "Melancholic", "Hopeful", "Mysterious", "Romantic", "Driving", "Eerie"];

export const ComposerPanel = ({ projectId, assets, onCreated, onAdd }) => {
  const [reference, setReference] = useState("");
  const [scene, setScene] = useState("");
  const [mood, setMood] = useState("");
  const [tempo, setTempo] = useState(90);
  const [instruments, setInstruments] = useState("");
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/generate/score", { project_id: projectId, reference, scene, mood, tempo, instruments });
      onCreated(data); toast.success(`Composing "${data.name}"`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="composer-panel">
      <Section title="Reference project">
        <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Film or TV show to draw the essence from" className="bg-[#0B0C10] h-9" data-testid="score-reference-input" />
        <div className="flex flex-wrap gap-1.5 mt-2">
          {REFS.map((r) => (
            <button key={r} onClick={() => setReference(r)} data-testid={`score-ref-${r.replace(/\W+/g, "-").toLowerCase()}`}
              className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${reference === r ? "border-[#D97706] text-[#fbbf24]" : "border-[#2A2E3D] hover:border-[#D97706]"}`}>{r}</button>
          ))}
        </div>
        <p className="text-[11px] text-slate-500 mt-2">We capture the vibe — instrumentation, tempo, harmony, texture — and write a brand-new original cue. No melodies are copied.</p>
      </Section>
      <Section title="Your scene">
        <Textarea value={scene} onChange={(e) => setScene(e.target.value)} placeholder="e.g. Astronaut drifts away from the station, realising she can't get back" className="bg-[#0B0C10] text-sm min-h-[72px]" data-testid="score-scene-input" />
        <div className="flex flex-wrap gap-1.5 mt-2">
          {MOODS.map((m) => (
            <button key={m} onClick={() => setMood(mood === m ? "" : m)} data-testid={`score-mood-${m.toLowerCase()}`}
              className={`text-[11px] px-2 py-1 rounded transition-colors ${mood === m ? "bg-[#D97706] text-[#0B0C10]" : "bg-[#0B0C10] text-slate-400 hover:text-white"}`}>{m}</button>
          ))}
        </div>
        <Param label="Tempo (BPM)" value={tempo} min={40} max={200} step={1} onChange={setTempo} testId="score-tempo" />
        <Input value={instruments} onChange={(e) => setInstruments(e.target.value)} placeholder="Instruments (optional): pipe organ, strings, synth pulse" className="bg-[#0B0C10] h-9 mt-2" data-testid="score-instruments-input" />
        <Button onClick={generate} disabled={busy || scene.trim().length < 2} className="w-full mt-3 bg-[#D97706] hover:bg-[#f08c0b] text-[#0B0C10]" data-testid="score-generate-button">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Music2 className="w-4 h-4 mr-2" />}Compose original score
        </Button>
      </Section>
      <Section title="Your cues">
        <AssetList assets={assets.filter((a) => a.kind === "score")} empty="Composed cues appear here." onAdd={onAdd} />
      </Section>
    </div>
  );
};
